/**
 * Carrying a referral code from a link, through signup, to the profile.
 *
 * The default registration path sends a paid plan choice STRAIGHT to Stripe,
 * and the payout fires on that first subscription — so a teacher who arrives
 * on an invite link and subscribes in under a minute (which is exactly what
 * the first real sale did) never has a moment to type a code in Settings. By
 * the time the card is charged, `referredByUid` is unset and the referral is
 * gone: the claim endpoint then refuses, because a code applied after the
 * first subscription could never be honoured.
 *
 * So the code has to travel on its own. It is stashed the same way the plan
 * choice is, because it faces the same problem — a Google sign-in redirect
 * leaves the site entirely and comes back with a bare URL.
 */

const STASH_KEY = 'luc.referral.code';

/** Read `?ref=CODE` off the current URL and remember it. Safe to call on every
 *  page load; a URL with no code leaves any existing stash alone. */
export function captureReferralFromUrl(search: string): string | null {
  try {
    const code = new URLSearchParams(search).get('ref');
    const clean = (code || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12);
    if (!clean) return null;
    sessionStorage.setItem(STASH_KEY, clean);
    return clean;
  } catch {
    // Private mode, blocked storage. The teacher can still type the code in.
    return null;
  }
}

export function readStashedReferral(): string | null {
  try {
    return sessionStorage.getItem(STASH_KEY) || null;
  } catch {
    return null;
  }
}

export function clearStashedReferral(): void {
  try {
    sessionStorage.removeItem(STASH_KEY);
  } catch { /* nothing to clear */ }
}

/** The link a teacher shares. */
export const referralLink = (code: string) =>
  `https://levelupclassroom.com/register?ref=${encodeURIComponent(code)}`;
